import React, { useEffect, useMemo, useState } from "react";
import { IconSearch, CatalogIcon, THEMES } from "../utils/iconSearch";

interface IconPickerProps {
  catalog: CatalogIcon[] | null;
  catalogError?: string;
  inUse: Set<string>; // icons already in this game
  usedBefore: Map<string, { label: string; recent: boolean }>; // icon -> where it was last used
  onPick: (file: string) => void;
  onDragIcon?: (e: React.DragEvent, file: string) => void;
}

const PAGE = 120;

// Corner marks use the game's own icon files as masks so they can be tinted.
// (Set inline: the build tool would try to bundle url() paths written in CSS.)
const MARK_ICONS = {
  "in-game": "/icons/check-circle-fill.svg",
  recent: "/icons/clock-fill.svg",
  used: "/icons/clock-counter-clockwise-fill.svg",
};
const markStyle = (kind: keyof typeof MARK_ICONS): React.CSSProperties => ({
  WebkitMaskImage: `url(${MARK_ICONS[kind]})`,
  maskImage: `url(${MARK_ICONS[kind]})`,
});

const IconPicker: React.FC<IconPickerProps> = ({ catalog, catalogError, inUse, usedBefore, onPick, onDragIcon }) => {
  const [query, setQuery] = useState("");
  const [theme, setTheme] = useState<string | null>(null);
  const [shown, setShown] = useState(PAGE);

  const search = useMemo(() => (catalog ? new IconSearch(catalog) : null), [catalog]);
  const results = useMemo(
    () => (search ? search.search(query, { theme }) : []),
    [search, query, theme]
  );

  useEffect(() => setShown(PAGE), [query, theme]);

  if (catalogError) return <div className="admin-message error">{catalogError}</div>;
  if (!catalog) return <div className="icon-picker-status">Loading icons…</div>;

  return (
    <div className="icon-picker">
      <div className="icon-picker-controls">
        <div className="icon-picker-search-row">
          <input
            type="search"
            className="admin-input icon-picker-search"
            placeholder={`Search ${catalog.length.toLocaleString()} icons, e.g. breakfast, ocean, party`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search icons"
          />
          <select
            className="icon-picker-theme-select"
            value={theme || ""}
            onChange={(e) => setTheme(e.target.value || null)}
            aria-label="Filter by theme"
          >
            <option value="">All themes</option>
            {THEMES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="icon-picker-count">
          {results.length === 0
            ? "No icons found. Try a broader word or another theme."
            : `${results.length.toLocaleString()} icon${results.length === 1 ? "" : "s"}`}
        </div>
      </div>

      <div className="icon-picker-grid">
        {results.slice(0, shown).map((icon) => {
          const used = inUse.has(icon.f);
          const prior = usedBefore.get(icon.f);
          return (
            <div
              key={icon.f}
              role="button"
              tabIndex={used ? -1 : 0}
              aria-disabled={used}
              onClick={() => !used && onPick(icon.f)}
              onKeyDown={(e) => {
                if (!used && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  onPick(icon.f);
                }
              }}
              className={`icon-tile${used ? " in-use" : ""}${prior?.recent ? " recent" : ""}`}
              draggable={!used && !!onDragIcon}
              onDragStart={(e) => onDragIcon && onDragIcon(e, icon.f)}
              title={
                used
                  ? `${icon.n}: already in this game`
                  : prior
                  ? `${icon.n}: used ${prior.recent ? "recently" : "before"} in ${prior.label}`
                  : icon.n
              }
            >
              <img src={`/icons/${icon.f}`} alt="" loading="lazy" draggable={false} />
              <span className="icon-tile-name">{icon.n}</span>
              {used && <span className="icon-tile-mark in-game" style={markStyle("in-game")} aria-label="Already in this game" />}
              {!used && prior && (
                <span
                  className={`icon-tile-mark ${prior.recent ? "recent" : "used"}`}
                  style={markStyle(prior.recent ? "recent" : "used")}
                  aria-label={prior.recent ? "Used in the last 4 weeks" : "Used in an earlier puzzle"}
                />
              )}
            </div>
          );
        })}
      </div>

      {results.length > shown && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button type="button" className="admin-button secondary" onClick={() => setShown((s) => s + PAGE)}>
            Show more ({(results.length - shown).toLocaleString()} left)
          </button>
        </div>
      )}
    </div>
  );
};

export default IconPicker;
